import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("plannio_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// --- Global GET de-duplication + short micro-cache -------------------------------------------
// Root cause of the dashboard request storm: dozens of components each fired their own raw
// api.get on first paint and again on every remount/renavigation, with no coordination. This
// wraps api.get so that (a) identical concurrent GETs share ONE network request (always safe),
// and (b) a GET result is reused for a few seconds to absorb remount/renavigation bursts. Any
// mutation (POST/PUT/PATCH/DELETE) clears the cache so reads after a write are always fresh.
// Requests with bespoke config (params, responseType, signal, custom headers, progress) bypass
// this entirely to avoid changing their semantics.
const _inflight = new Map();
const _cache = new Map();
const GET_TTL = 5000;

// Session-tier caching for genuinely global shell data. Measured 2026-07-11: 12 of these
// endpoints re-fired on EVERY client-side navigation because layout widgets remount. They
// describe slow-moving state (plan, capacity, announcements, config), so they are cached for
// minutes rather than seconds. Safety: ANY mutation (POST/PUT/PATCH/DELETE, including login)
// still clears the whole cache, so reads after any user action are always fresh, and pollers
// that need live data use cachedGet(url, { fresh: true }).
const TTL_RULES = [
  [/^\/(whats-new|legal\/updates|subscriptions\/post-removal|secure-shares\/config|consents\/cookie-config|platform\/(modules|calendar-sources))/, 30 * 60 * 1000],
  [/^\/(subscription$|household\/capacity$|storage$|household\/members$|hubs$|onboarding\/me|planni\/context)/, 5 * 60 * 1000],
  [/^\/connect\/unread-summary/, 60 * 1000],
];
const _ttlFor = (url) => {
  for (const [re, ttl] of TTL_RULES) if (re.test(url)) return ttl;
  return GET_TTL;
};

const _cacheable = (config) => {
  if (!config) return true;
  const keys = Object.keys(config);
  const allowed = new Set(["headers"]); // interceptor-added auth header is fine
  return keys.every((k) => allowed.has(k));
};

const _rawGet = api.get.bind(api);
api.get = (url, config) => {
  if (typeof url !== "string" || !_cacheable(config)) return _rawGet(url, config);
  const now = Date.now();
  const hit = _cache.get(url);
  if (hit && now - hit.t < _ttlFor(url)) return Promise.resolve(hit.res);
  if (_inflight.has(url)) return _inflight.get(url);
  const p = _rawGet(url, config)
    .then((res) => { _cache.set(url, { t: Date.now(), res }); _inflight.delete(url); return res; })
    .catch((e) => { _inflight.delete(url); throw e; });
  _inflight.set(url, p);
  return p;
};

// Any write invalidates the read cache so the app never shows stale data after a mutation.
["post", "put", "patch", "delete"].forEach((m) => {
  const raw = api[m].bind(api);
  api[m] = (...args) => { _cache.clear(); _inflight.clear(); return raw(...args); };
});

// Seed the GET micro-cache from a consolidated response (GET /session/boot). Entries mimic a
// real axios response, so every existing api.get caller for these URLs is served instantly
// without a network request. Normal TTL rules apply, so nothing lives longer than it would
// have if fetched directly.
export function primeCache(url, data) {
  if (data === undefined || data === null) return;
  _cache.set(url, { t: Date.now(), res: { data, status: 200, headers: {}, config: { url } } });
}

// Instant-paint seed for the reminder detail page: find the reminder in any cached list
// response (e.g. GET /reminders) so the page renders immediately while the full detail
// (with attached documents) loads in the background. Mutations clear the cache, so a
// stale seed can never survive a write.
export function peekReminder(id) {
  for (const { res } of _cache.values()) {
    const data = res?.data;
    const list = Array.isArray(data) ? data : Array.isArray(data?.reminders) ? data.reminders : null;
    if (!list) continue;
    const hit = list.find((x) => x && x.id === id && x.due_date && x.title);
    if (hit) return { ...hit, __seeded: true };
  }
  return null;
}


export function formatApiError(detail) {
  if (detail == null) return "That didn't work. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return "That didn't work. Please try again.";
}

export const CATEGORY_META = {
  pets: { label: "Pets", color: "var(--tone-green-fg)", bg: "var(--tone-green-bg)", icon: "PawPrint" },
  home: { label: "Home", color: "var(--tone-blue-fg)", bg: "var(--tone-blue-bg)", icon: "Home" },
  vehicle: { label: "Vehicle", color: "var(--tone-orange-fg)", bg: "var(--tone-orange-bg)", icon: "Car" },
  community: { label: "Roadmap", color: "var(--tone-purple-fg, #7C3AED)", bg: "var(--tone-purple-bg, rgba(124,58,237,0.1))", icon: "Sparkles" },
  picture: { label: "Picture", color: "var(--tone-purple-fg, #7C3AED)", bg: "var(--tone-purple-bg, rgba(124,58,237,0.1))", icon: "Image" },
  travel: { label: "Travel", color: "#0F766E", bg: "rgba(15,118,110,0.1)", icon: "Plane" },
  connect: { label: "Connect", color: "#0F766E", bg: "rgba(15,118,110,0.1)", icon: "Handshake" },
  baby: { label: "Baby Hub", color: "var(--tone-pink-fg, #BE185D)", bg: "var(--tone-pink-bg, rgba(244,114,182,0.12))", icon: "Baby" },
  budget: { label: "Budget", color: "#0F766E", bg: "rgba(15,118,110,0.1)", icon: "PiggyBank" },
};

export const categoryMeta = (c) =>
  CATEGORY_META[c] || { label: c || "Other", color: "var(--tone-neutral-fg, #636E72)", bg: "var(--tone-neutral-bg, #F4F6F2)", icon: "Bell" };

// Curated palette for calendar colour personalisation. Each swatch pairs a foreground colour with
// a translucent background so labels stay legible in both light and dark themes.
export const CAL_PALETTE = {
  teal: { label: "Teal", color: "#0F766E", bg: "rgba(15,118,110,0.12)" },
  blue: { label: "Blue", color: "#2563EB", bg: "rgba(37,99,235,0.12)" },
  green: { label: "Green", color: "#15803D", bg: "rgba(21,128,61,0.12)" },
  amber: { label: "Amber", color: "#B45309", bg: "rgba(180,83,9,0.12)" },
  rose: { label: "Rose", color: "#BE185D", bg: "rgba(190,24,93,0.12)" },
  purple: { label: "Purple", color: "#7C3AED", bg: "rgba(124,58,237,0.12)" },
  slate: { label: "Slate", color: "#475569", bg: "rgba(71,85,105,0.12)" },
  red: { label: "Red", color: "#DC2626", bg: "rgba(220,38,38,0.12)" },
};

// Categories a user may recolour on the calendar (hub-level).
export const CAL_COLOUR_CATEGORIES = [
  { key: "pets", label: "Pets" },
  { key: "home", label: "Home" },
  { key: "vehicle", label: "Vehicle" },
  { key: "travel", label: "Travel" },
  { key: "baby", label: "Baby Hub" },
];

// Resolve the effective colour meta for a calendar category, honouring a user's personalised
// overrides while keeping the original label/icon.
export const calendarMeta = (c, overrides = {}) => {
  const base = categoryMeta(c);
  const swatch = overrides[c] && CAL_PALETTE[overrides[c]];
  return swatch ? { ...base, color: swatch.color, bg: swatch.bg } : base;
};

export const downloadUrl = (docId) =>
  `${API}/documents/${docId}/download?auth=${localStorage.getItem("plannio_token") || ""}`;

export const zipDownloadUrl = (ids) => {
  const token = localStorage.getItem("plannio_token") || "";
  const idsPart = ids && ids.length ? `ids=${ids.join(",")}&` : "";
  return `${API}/documents/download-zip?${idsPart}auth=${token}`;
};

// Secure Share (public recipient) helpers - no auth; access is a short-lived unlock token.
export const publicShareItemUrl = (token, itemId, access = "", download = false) => {
  const parts = [];
  if (access) parts.push(`access=${encodeURIComponent(access)}`);
  if (download) parts.push("download=true");
  return `${API}/public/shares/${token}/items/${itemId}${parts.length ? "?" + parts.join("&") : ""}`;
};
export const shareLinkFor = (token) => `${window.location.origin}/s/${token}`;

export const formatBytes = (bytes = 0) => {
  if (!bytes) return "0MB";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)}GB`;
  if (mb >= 10) return `${Math.round(mb)}MB`;
  return `${mb.toFixed(1)}MB`;
};

// GBP. Yearly total = monthly * 12 * 0.8 (20% off the 12-month total).
export const PLAN_INFO = {
  free: {
    key: "free", name: "Free", tagline: "Get started with the essentials.", monthly: 0, accent: "#0F766E",
    features: ["1 Pet, 1 Vehicle, 1 Home, 1 Budget", "100MB document vault", "In-app notifications", "Basic templates", "Calendar & Vault access"],
  },
  premium: {
    key: "premium", name: "Premium", tagline: "Everything you need to stay organised.", monthly: 4.99, accent: "#0F766E", popular: true,
    features: ["Unlimited Pets, Vehicles, Homes & Budgets", "5GB document vault", "Email & SMS notifications", "Advanced templates", "Custom & saved reminder types", "Unlimited custom calendar events", "Export & backup data", "Access to future modules"],
  },
  family: {
    key: "family", name: "Family", tagline: "For households that do it together.", monthly: 7.99, accent: "#7C3AED",
    features: ["Everything in Premium", "10GB document vault", "Household sharing", "Invite family members", "Shared calendar & timeline", "Shared responsibilities"],
  },
};

export const yearlyPerMonth = (monthly) => +(monthly * 0.8).toFixed(2);
// Annual total is the displayed (rounded) per-month price times 12, so the headline yearly figure
// always reconciles exactly with the "equivalent per month" shown to the user (e.g. £3.99 x 12 = £47.88).
export const yearlyTotal = (monthly) => +(yearlyPerMonth(monthly) * 12).toFixed(2);

// Record an item open for the Dashboard "Recently Viewed" section. Best-effort, never throws.
export const recordView = (entry) => { try { api.post("/recently-viewed", entry).catch(() => {}); } catch {} };

// Show a Sonner toast with a 10s Undo button. `onUndo` is called if the user clicks Undo.
// Import { toast } from "sonner" at the call site and pass it in to avoid a hard dependency here.
export const undoToast = (toast, message, onUndo, { duration = 10000 } = {}) => {
  toast.success(message, {
    duration,
    action: { label: "Undo", onClick: () => { try { onUndo(); } catch {} } },
  });
};

// cachedGet now simply delegates to the globally de-duplicated api.get (see top of file). It is
// retained for call sites that pass { fresh: true } to force a network read (bypassing cache).
export const cachedGet = (url, { fresh = false } = {}) => {
  if (fresh) { _cache.delete(url); _inflight.delete(url); }
  return api.get(url);
};
export const invalidateCache = (url) => {
  if (url) { _cache.delete(url); _inflight.delete(url); } else { _cache.clear(); _inflight.clear(); }
};

export default api;
